import React, { useState, useEffect, useCallback } from 'react';
import { useAcademicData } from './useAcademicData';
import SubjectModal from './SubjectModal';
import AcademicContextSelector from './AcademicContextSelector';
import SubjectFacultyAssignment from './SubjectFacultyAssignment';
import BulkUploadModal from './BulkUploadModal';
import CopySubjectsModal from './CopySubjectsModal';
import api from '../../api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Initial form state
const initialForm = {
  name: '',
  subjectCode: '',
  subjectCategory: 'academic',
  subjectType: 'theory',
  customSubjectType: '',
  isCreditBased: false,
  credits: 0,
  requiresAttendance: true,
  appearsInTimetable: true,
  assessmentType: 'marks',
  subjectManagementType: 'common',
  electiveType: '',
  languageType: '',
  requiresGroupManagement: false
};

const SubjectManager = ({
  department,
  onCreateSubject,
  onEditSubject,
  onDeleteSubject,
  onAssignFacultyToSubject,
  onViewFacultyForSubject,
  loadingStates = {},
  token = null
}) => {
  // State management
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [viewedSubject, setViewedSubject] = useState(null);
  
  // Academic context state
  const [academicContext, setAcademicContext] = useState({
    academicYearId: '',
    academicYear: null,
    semesterId: null,
    semester: null,
    termId: null,
    term: null,
    isSemesterBased: false,
    academicYearName: '',
    terms: []
  });

  // Use academic data hook for terms
  const {
   

    setSelectedAcademicYear,
  } = useAcademicData(department?.tenantId, token);
  
  // Subjects data
  const [subjects, setSubjects] = useState({
    all: [],
    grouped: {
      semesterGroups: [],
      annual: []
    },
    summary: {
      total: 0,
      bySemester: 0,
      annual: 0
    }
  });

  // Faculty assignment state
  const [viewFacultySubject, setViewFacultySubject] = useState(null);
  const [showFacultyModal, setShowFacultyModal] = useState(false);
  const [assignedFacultyList, setAssignedFacultyList] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState('');
  const [facultySearch, setFacultySearch] = useState('');
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  
  // Assign faculty modal
  const [assigningSubject, setAssigningSubject] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);

  // New features state
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showCopySubjectsModal, setShowCopySubjectsModal] = useState(false);

  // Multi-select state
  const [selectedSubjects, setSelectedSubjects] = useState(new Set());
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  // Auto-clear messages
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  useEffect(() => {
    if (viewError) {
      const timer = setTimeout(() => {
        setViewError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [viewError]);

  // Helper to extract ID from MongoDB ObjectId format
  const extractId = useCallback((obj) => {
    if (!obj) return null;
    if (typeof obj === 'string') return obj;
    if (typeof obj === 'object') {
      // Handle MongoDB $oid format
      if (obj.$oid) return obj.$oid;
      // Handle populated objects
      if (obj._id) {
        if (typeof obj._id === 'object' && obj._id.$oid) return obj._id.$oid;
        return obj._id;
      }
      if (obj.id) return obj.id;
    }
    return null;
  }, []);


    // Manual grouping function
  const groupSubjectsManually = useCallback((subjects) => {
    const grouped = {
      semesterGroups: [],
      annual: []
    };

    if (!Array.isArray(subjects)) return grouped;

    const semesterMap = new Map();
    const termMap = new Map();
    
    subjects.forEach(subject => {
      const semesterId = extractId(subject.semesterId);
      const termId = extractId(subject.termId);
      
      if (semesterId && !subject.isCrossSemester) {
        if (!semesterMap.has(semesterId)) {
          semesterMap.set(semesterId, {
            semester: subject.semesterId,
            subjects: []
          });
        }
        semesterMap.get(semesterId).subjects.push(subject);
      } else if (termId) {
        if (!termMap.has(termId)) {
          termMap.set(termId, {
            term: subject.termId,
            subjects: []
          });
        }
        termMap.get(termId).subjects.push(subject);
      } else {
        grouped.annual.push(subject);
      }
    });

    grouped.semesterGroups = Array.from(semesterMap.values()).sort((a, b) => {
      const aNum = a.semester?.semesterNumber || 0;
      const bNum = b.semester?.semesterNumber || 0;
      return aNum - bNum;
    });

    return grouped;
  }, [extractId]);

  // Fetch subjects from API
  const fetchSubjects = useCallback(async (silent = false) => {
    if (!department?.tenantId || !department?._id || !academicContext.academicYearId) return;
    
    if (!silent) {
      setLoading(true);
      setError('');
    }
    
    try {
      const params = { 
        tenantId: department.tenantId,
        academicYearId: academicContext.academicYearId
      };
      
      if (academicContext.semesterId) {
        params.semesterId = academicContext.semesterId;
      }
      if (academicContext.termId) {
        params.termId = academicContext.termId;
      }

      const response = await api.get(`/api/subjects/get-subjects/${department._id}`, { params });
      
      if (response.data) {
        let allSubjects = [];
        let groupedData = { semesterGroups: [], annual: [] };
        
        // Handle different response formats
        if (response.data.subjects) {
          if (Array.isArray(response.data.subjects)) {
            allSubjects = response.data.subjects;
          } else if (response.data.subjects.all && Array.isArray(response.data.subjects.all)) {
            allSubjects = response.data.subjects.all;
            groupedData = response.data.subjects.grouped || groupedData;
          }
        } else if (response.data.data && Array.isArray(response.data.data)) {
          allSubjects = response.data.data;
        } else if (Array.isArray(response.data)) {
          allSubjects = response.data;
        }

        // Process subjects to ensure consistent ID format
        const processedSubjects = allSubjects.map(subject => ({
          ...subject,
          _id: extractId(subject) || subject._id,
          academicYearId: subject.academicYearId ? {
            ...(typeof subject.academicYearId === 'object' ? subject.academicYearId : {}),
            _id: extractId(subject.academicYearId)
          } : null,
          semesterId: subject.semesterId ? {
            ...(typeof subject.semesterId === 'object' ? subject.semesterId : {}),
            _id: extractId(subject.semesterId)
          } : null,
          termId: subject.termId ? {
            ...(typeof subject.termId === 'object' ? subject.termId : {}),
            _id: extractId(subject.termId)
          } : null
        }));

        // Group subjects if grouping data not provided
        if (!groupedData.semesterGroups.length && !groupedData.annual.length) {
          groupedData = groupSubjectsManually(processedSubjects);
        }

        setSubjects({
          all: processedSubjects,
          grouped: groupedData,
          summary: {
            total: processedSubjects.length,
            bySemester: groupedData.semesterGroups.reduce((acc, group) => acc + (group.subjects?.length || 0), 0),
            annual: groupedData.annual?.length || 0
          }
        });
      }
    } catch (err) {
      console.error('Error fetching subjects:', err);
      if (!silent) {
        const errorMsg = err?.response?.data?.message || 'Failed to load subjects';
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [department, academicContext.academicYearId, academicContext.semesterId, academicContext.termId, extractId, groupSubjectsManually]);



  // Fetch subjects when academic context changes
  useEffect(() => {
    if (academicContext.academicYearId && department?._id && department?.tenantId) {
      fetchSubjects();
    } else {
      setSubjects({
        all: [],
        grouped: {
          semesterGroups: [],
          annual: []
        },
        summary: {
          total: 0,
          bySemester: 0,
          annual: 0
        }
      });
    }
  }, [academicContext.academicYearId, academicContext.semesterId, academicContext.termId, department, fetchSubjects]);

  // Reset selection when subjects change
  useEffect(() => {
    setSelectedSubjects(new Set());
    setIsSelectAll(false);
  }, [subjects.all]);

  // Academic context change handler
  const handleAcademicContextChange = useCallback((context) => {
    console.log('Academic context changed:', context);
    setAcademicContext({
      academicYearId: context.academicYearId,
      academicYear: context.academicYear,
      semesterId: context.semesterId,
      semester: context.semester,
      termId: context.termId,
      term: context.term,
      isSemesterBased: context.isSemesterBased,
      academicYearName: context.academicYear?.name || '',
      terms: context.terms || []
    });
    // Sync with useAcademicData
    if (context.academicYearId) {
      setSelectedAcademicYear(context.academicYearId);
    }
  }, [setSelectedAcademicYear]);

  // Handle Add Subject click
  const handleAddSubjectClick = () => {
    setForm(initialForm);
    setEditingId(null);
    setShowSubjectModal(true);
  };

  // Handle Edit Subject
  const handleEdit = (subject) => {
    console.log('Editing subject:', subject);
    
    // Extract IDs using helper
    const extractIdFromField = (field) => {
      if (!field) return null;
      if (typeof field === 'string') return field;
      if (typeof field === 'object') {
        if (field.$oid) return field.$oid;
        if (field._id) {
          if (typeof field._id === 'object' && field._id.$oid) return field._id.$oid;
          return field._id;
        }
        if (field.id) return field.id;
      }
      return null;
    };

    // Extract all IDs from the subject
    const semesterId = extractIdFromField(subject.semesterId);
    const termId = extractIdFromField(subject.termId);
    const academicYearId = extractIdFromField(subject.academicYearId);

    const isElectiveOrLanguage = subject.subjectType === 'elective' || subject.subjectType === 'language';
    const subjectManagementType = isElectiveOrLanguage 
      ? (subject.subjectManagementType || (subject.requiresGroupManagement ? 'choice-based' : 'common'))
      : 'common';

    const formData = {

      name: subject.name || '',
      subjectCode: subject.subjectCode || '',
      subjectCategory: subject.subjectCategory || 'academic',
      subjectType: subject.subjectType || 'theory',
      customSubjectType: subject.customSubjectType || '',
      isCreditBased: subject.isCreditBased || false,
      credits: subject.credits || 0,
      requiresAttendance: subject.requiresAttendance !== undefined ? subject.requiresAttendance : true,
      appearsInTimetable: subject.appearsInTimetable !== undefined ? subject.appearsInTimetable : true,
      assessmentType: subject.assessmentType !== undefined && subject.assessmentType !== null ? subject.assessmentType : 'both',
      subjectManagementType: subjectManagementType,
      electiveType: subject.electiveType || '',
      languageType: subject.languageType || '',
      requiresGroupManagement: subject.requiresGroupManagement || false,
      semesterId: semesterId,
      termId: termId,
      academicYearId: academicYearId,
      // Ensure marks fields are included for edit mode
      maxMarks: subject.maxMarks !== undefined && subject.maxMarks !== null ? subject.maxMarks : '',
      cieMaxMarks: subject.cieMaxMarks !== undefined && subject.cieMaxMarks !== null ? subject.cieMaxMarks : '',
      endExamMaxMarks: subject.endExamMaxMarks !== undefined && subject.endExamMaxMarks !== null ? subject.endExamMaxMarks : ''
    };

    setForm(formData);
    setEditingId(subject._id);
    setShowSubjectModal(true);
    setError('');
    setSuccess('');
  };

  // Handle subject submission
  const handleSubmit = async (subjectData) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Basic validations
      const name = (subjectData.name || '').trim();
      const subjectCode = (subjectData.subjectCode || '').trim();
      if (!department?._id) {
        throw new Error('Missing department.');
      }
      if (!department?.tenantId) {
        throw new Error('Missing tenant.');
      }
      if (!subjectData.academicYearId) {
        throw new Error('Select an academic year.');
      }
      if (!name) {
        throw new Error('Subject name is required.');
      }
      if (!subjectCode) {
        throw new Error('Subject code is required.');
      }

      // Validate based on academic year type
      if (subjectData.isSemesterBased) {
        if (!subjectData.semesterId) {
          throw new Error('Please select a semester for this subject.');
        }
      } else {
        if (!subjectData.termId) {
          throw new Error('Please select a term for this subject.');
        }
      }

      // Validate management type for elective/language subjects
      if ((subjectData.subjectType === 'elective' || subjectData.subjectType === 'language') && 
          !subjectData.subjectManagementType) {
        throw new Error('Please select subject management type for elective or language subjects.');
      }


      // Normalize payload for backend (ensure marks fields are included)
      const payload = {
        name,
        subjectCode,
        subjectCategory: subjectData.subjectCategory || 'academic',
        subjectType: subjectData.subjectType || 'theory',
        customSubjectType: subjectData.customSubjectType || '',
        requiresAttendance: subjectData.requiresAttendance !== false,
        appearsInTimetable: subjectData.appearsInTimetable !== false,
        isCreditBased: !!subjectData.isCreditBased,
        credits: subjectData.isCreditBased ? Number(subjectData.credits || 0) : 0,
        subjectManagementType: (subjectData.subjectType === 'elective' || subjectData.subjectType === 'language')
          ? (subjectData.subjectManagementType || 'common')
          : 'common',
        electiveType: subjectData.electiveType || '',
        languageType: subjectData.languageType || '',
        requiresGroupManagement: (subjectData.subjectType === 'elective' || subjectData.subjectType === 'language')
          ? subjectData.requiresGroupManagement
          : false,
        departmentId: department._id,
        tenantId: department.tenantId,
        academicYearId: subjectData.academicYearId,
        isSemesterBased: subjectData.isSemesterBased || false,
        // MARKS FIELDS
        maxMarks: Number(subjectData.maxMarks),
        cieMaxMarks: Number(subjectData.cieMaxMarks),
        endExamMaxMarks: Number(subjectData.endExamMaxMarks),
        assessmentType: subjectData.assessmentType
      };

      // Add semester or term ID based on context
      if (subjectData.semesterId) {
        payload.semesterId = subjectData.semesterId;
      }
      if (subjectData.termId) {
        payload.termId = subjectData.termId;
      }

      if (editingId) {
        // UPDATE
        const updatePayload = {
          ...payload,
          subjectName: name,
        };

        await api.put(
          `/api/subjects/edit-subject/${editingId}`,
          updatePayload,
          {
            headers: token
              ? { Authorization: `Bearer ${token}` }
              : undefined
          }
        );

        setSuccess('Subject updated successfully!');
        toast.success('Subject updated successfully!');
      } else {
        // CREATE
        const createPayload = {
          ...payload,
          subjectName: name,
          ...(subjectData.createdBy ? { createdBy: subjectData.createdBy } : {})
        };

        await api.post(
          '/api/subjects/add-subject',
          createPayload,
          {
            headers: token
              ? { Authorization: `Bearer ${token}` }
              : undefined
          }
        );
        
        setSuccess('Subject created successfully!');
        toast.success('Subject created successfully!');
      }

      await fetchSubjects(true);
      setForm(initialForm);
      setEditingId(null);
      setShowSubjectModal(false);
    } catch (err) {
      console.error('Error saving subject:', err);
      const serverMsg = err?.response?.data?.message;
      const msg = serverMsg || err.message || 'Failed to save subject';
      setError(msg);
      toast.error(msg);
      
      // Dispatch error event for modal to handle
      window.dispatchEvent(new CustomEvent('subjectManagerError', { 
        detail: { message: msg, errors: err?.response?.data?.errors } 
      }));
    } finally {
      setLoading(false);
    }
  };

  // Delete subject
  const handleDelete = async (id) => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      if (onDeleteSubject) {
        await onDeleteSubject(id);
      } else {
        await api.delete(`/api/subjects/delete-subject/${id}`, {
          data: { 
            tenantId: department.tenantId, 
            departmentId: department._id 
          }
        });
      }
      
      setSuccess('Subject deleted successfully!');
      toast.success('Subject deleted successfully!');
      setConfirmDeleteId(null);
      
      await fetchSubjects(true);
    } catch (err) {
      console.error('Error deleting subject:', err);
      const errorMsg = err?.response?.data?.message || 'Failed to delete subject';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // View assigned faculty
  const handleViewFaculty = async (subject) => {
    const subjectId = subject?._id || subject?.id;
    if (!subjectId) {
      setViewError('Subject ID missing');
      return;
    }

    setViewFacultySubject(subject);
    setAssignedFacultyList([]);
    setViewError('');
    setShowFacultyModal(true);
    setViewLoading(true);

    try {
      let facultyList = [];
      if (onViewFacultyForSubject) {
        facultyList = await onViewFacultyForSubject(subject);
      } else {
        const response = await api.get(`/api/subjects/get-faculty-by-subject/${subjectId}/${department.tenantId}`);
        facultyList = Array.isArray(response.data?.faculty) ? response.data.faculty : 
                     Array.isArray(response.data) ? response.data : [];
      }

      const normalizedFaculty = (facultyList || []).map(f => ({
        _id: f._id || f.id || f.userId,
        name: f.name || f.fullName || `${f.firstName || ''} ${f.lastName || ''}`.trim() || 'Unknown Faculty',
        email: f.email || f.username || ''
      }));

      setAssignedFacultyList(normalizedFaculty);
    } catch (err) {
      console.error('Error fetching assigned faculty:', err);
      const errorMsg = err?.response?.data?.message || 'Failed to load assigned faculty';
      setViewError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setViewLoading(false);
    }
  };

  // Remove assigned faculty
  const removeAssignedFaculty = async (facultyId) => {
    if (!viewFacultySubject) return;
    
    setViewLoading(true);
    setViewError('');
    
    try {
      await api.put(`/api/subjects/remove-faculty/${viewFacultySubject._id}`, {
        facultyId,
        tenantId: department.tenantId,
        departmentId: department._id
      });

      setAssignedFacultyList(prev => prev.filter(f => String(f._id) !== String(facultyId)));
      setConfirmRemoveId(null);
      toast.success('Faculty removed successfully');
    } catch (err) {
      console.error('Error removing faculty:', err);
      const errorMsg = err?.response?.data?.message || 'Failed to remove faculty';
      setViewError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setViewLoading(false);
    }
  };

  // Multi-select handlers
  const handleSelectSubject = (subjectId) => {
    const newSelected = new Set(selectedSubjects);
    if (newSelected.has(subjectId)) {
      newSelected.delete(subjectId);
    } else {
      newSelected.add(subjectId);
    }
    setSelectedSubjects(newSelected);
    
    if (newSelected.size === subjects.all.length && subjects.all.length > 0) {
      setIsSelectAll(true);
    } else {
      setIsSelectAll(false);
    }
  };

  const handleSelectAll = () => {
    if (isSelectAll || selectedSubjects.size === subjects.all.length) {
      setSelectedSubjects(new Set());
      setIsSelectAll(false);
    } else {
      const allIds = subjects.all.map(subject => subject._id);
      setSelectedSubjects(new Set(allIds));
      setIsSelectAll(true);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedSubjects.size === 0) return;
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const subjectIds = Array.from(selectedSubjects);
      
      if (onDeleteSubject) {
        for (const subjectId of subjectIds) {
          await onDeleteSubject(subjectId);
        }
      } else {
        await api.post('/api/subjects/bulk-delete', {
          subjectIds,
          tenantId: department.tenantId,
          departmentId: department._id
        });
      }
      
      setSuccess(`${subjectIds.length} subject${subjectIds.length > 1 ? 's' : ''} deleted successfully!`);
      toast.success(`${subjectIds.length} subject${subjectIds.length > 1 ? 's' : ''} deleted successfully!`);
      
      setSelectedSubjects(new Set());
      setIsSelectAll(false);
      setShowBulkDeleteModal(false);
      
      await fetchSubjects(true);
    } catch (err) {
      console.error('Error deleting subjects:', err);
      const errorMsg = err?.response?.data?.message || 'Failed to delete subjects';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Helper to get user ID from storage
  const getUserIdFromStorage = () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      if (!token) return null;
      const payload = JSON.parse(window.atob(token.split('.')[1]));
      return payload.userId || payload.user_id || payload.sub || null;
    } catch {
      return null;
    }
  };

  // Get context display name
  const getContextDisplayName = () => {
    if (!academicContext.academicYearId) return 'No context selected';
    
    let display = academicContext.academicYearName || 'Academic Year';
    
    if (academicContext.isSemesterBased) {
      if (academicContext.semesterId) {
        display += ` - ${academicContext.semester?.semesterName || 'Semester'}`;
      } else {
        display += ' - All Semesters';
      }
    } else {
      if (academicContext.termId) {
        display += ` - ${academicContext.term?.termLabel || academicContext.term?.termName || 'Term'}`;
      } else {
        display += ' - Annual System';
      }
    }
    
    return display;
  };

  // Helper functions for rendering
  const getSubjectTypeBadge = (subject) => {
    let typeLabel = 'Theory';
    let typeColor = 'gray';
    
    if (subject.subjectType) {
      switch (subject.subjectType) {
        case 'lab':
          typeLabel = 'Lab';
          typeColor = 'blue';
          break;
        case 'elective':
          if (subject.electiveType === 'choice-based' || subject.requiresGroupManagement) {
            typeLabel = 'Elective (Choice-Based)';
            typeColor = 'purple';
          } else {
            typeLabel = 'Elective (Common)';
            typeColor = 'green';
          }
          break;
        case 'language':
          if (subject.languageType === 'choice-based' || subject.requiresGroupManagement) {
            typeLabel = 'Language (Choice-Based)';
            typeColor = 'purple';
          } else {
            typeLabel = 'Language (Common)';
            typeColor = 'green';
          }
          break;
        case 'project':
          typeLabel = 'Project';
          typeColor = 'orange';
          break;
        case 'custom':
          typeLabel = subject.customSubjectType || 'Custom';
          typeColor = 'indigo';
          break;
        default:
          typeLabel = 'Theory';
          typeColor = 'gray';
      }
    }

    const category = subject.subjectCategory === 'academic' ? { label: 'Academic', color: 'blue' } :
                    subject.subjectCategory === 'non_academic' ? { label: 'Non-Academic', color: 'red' } :
                    { label: 'Extension', color: 'green' };

    const colorMap = {
      blue: { bg: 'bg-blue-100', text: 'text-blue-800' },
      green: { bg: 'bg-green-100', text: 'text-green-800' },
      purple: { bg: 'bg-purple-100', text: 'text-purple-800' },
      orange: { bg: 'bg-orange-100', text: 'text-orange-800' },
      gray: { bg: 'bg-gray-100', text: 'text-gray-800' },
      red: { bg: 'bg-red-100', text: 'text-red-800' },
      indigo: { bg: 'bg-indigo-100', text: 'text-indigo-800' }
    };
    
    const typeCls = colorMap[typeColor] || colorMap.gray;
    const catCls = colorMap[category.color] || colorMap.gray;

    return (
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeCls.bg} ${typeCls.text}`}>
          {typeLabel}
        </span>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${catCls.bg} ${catCls.text}`}>
          {category.label}
        </span>
        {subject.isCreditBased && subject.credits && subject.credits > 0 && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
            {subject.credits} Cr
          </span>
        )}
        {!subject.requiresAttendance && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            No Attendance
          </span>
        )}
      </div>
    );
  };

  const getSubjectContextInfo = (subject) => {
    const isAnnual = !subject.academicYearId?.isSemesterBased && !subject.semesterId && !subject.termId;
    const semester = subject.semesterId;
    const term = subject.termId;

    let badge = null;

    if (isAnnual) {
      badge = (
        <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs">
          Annual System
        </span>
      );
    } else if (semester && (semester.semesterName || semester.semesterNumber)) {
      badge = (
        <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs">
          {semester.semesterName || `Semester ${semester.semesterNumber}`}
        </span>
      );
    } else if (term && (term.termLabel || term.termName || term.termNumber)) {
      badge = (
        <span className="inline-flex items-center px-2 py-1 rounded-full bg-purple-100 text-purple-700 text-xs">
          {term.termLabel || term.termName || `Term ${term.termNumber}`}
        </span>
      );
    }

    return { isAnnual, badge };
  };

  const getSubjectIcon = (subject) => {
    const type = subject.subjectType || 'theory';
    const icons = {
      theory: '📚',
      lab: '🔬',
      elective: '📖',
      language: '🗣️',
      project: '📋',
      custom: '✏️',
      ncc: '🎖️',
      nss: '🤝',
      sports: '⚽',
      cultural: '🎭'
    };
    return icons[type] || icons.theory;
  };

  const renderSemesterGroups = () => {
    if (!Array.isArray(subjects.grouped.semesterGroups) || subjects.grouped.semesterGroups.length === 0) return null;

    return subjects.grouped.semesterGroups.map((group, index) => (
      <div key={group.semester?._id || index} className="mb-6">
        <div className="flex items-center justify-between mb-4 p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl border border-blue-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-semibold text-sm">S{group.semester?.semesterNumber || index + 1}</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {group.semester?.semesterName || `Semester ${group.semester?.semesterNumber}`}
              </h3>
              <p className="text-sm text-blue-600">
                {Array.isArray(group.subjects) ? group.subjects.length : 0} subject{Array.isArray(group.subjects) && group.subjects.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="text-xs text-blue-700 bg-blue-100 px-3 py-1.5 rounded-full font-medium">
            Semester {group.semester?.semesterNumber}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Array.isArray(group.subjects) && group.subjects.map(subject => renderSubjectCard(subject))}
        </div>
      </div>
    ));
  };

  const renderTermGroups = () => {
    if (!academicContext.terms || academicContext.terms.length === 0) return null;
    
    const termMap = new Map();
    subjects.all.forEach(subject => {
      if (subject.termId) {
        const termId = typeof subject.termId === 'object' ? subject.termId._id || subject.termId.id : subject.termId;
        if (!termMap.has(termId)) {
          termMap.set(termId, {
            term: subject.termId,
            subjects: []
          });
        }
        termMap.get(termId).subjects.push(subject);
      }
    });
    
    return academicContext.terms.map((term, idx) => {
      const key = term._id || term.id;
      const group = termMap.get(key) || { term, subjects: [] };
      return (
        <div key={key} className="mb-6">
          <div className="flex items-center justify-between mb-4 p-4 bg-gradient-to-r from-indigo-50 to-indigo-100 rounded-xl border border-indigo-200">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-semibold text-sm">T{term.termNumber || idx + 1}</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {term.termLabel || term.termName || `Term ${term.termNumber || idx + 1}`}
                </h3>
                <p className="text-sm text-indigo-600">
                  {group.subjects.length} subject{group.subjects.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <div className="text-xs text-indigo-700 bg-indigo-100 px-3 py-1.5 rounded-full font-medium">
              {term.termLabel || term.termName || `Term ${term.termNumber || idx + 1}`}
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {group.subjects.map(subject => renderSubjectCard(subject))}
          </div>
        </div>
      );
    });
  };

  const renderAnnualSubjects = () => {
    if (!Array.isArray(subjects.grouped.annual) || subjects.grouped.annual.length === 0) return null;

    return (
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4 p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-xl border border-green-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-semibold text-sm">A</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Annual System Subjects</h3>
              <p className="text-sm text-green-600">
                {subjects.grouped.annual.length} subject{subjects.grouped.annual.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="text-xs text-green-700 bg-green-100 px-3 py-1.5 rounded-full font-medium">
            Annual Program
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {subjects.grouped.annual.map(subject => renderSubjectCard(subject))}
        </div>
      </div>
    );
  };

  const renderSubjectCard = (subject) => {
    if (!subject) return null;
    
    const contextInfo = getSubjectContextInfo(subject);
    const isSelected = selectedSubjects.has(subject._id);
    
    return (
      <div key={subject._id} className={`bg-white rounded-xl border ${isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'} p-5 hover:shadow-lg transition-all duration-200 hover:border-blue-300 relative`}>
        <div className="absolute top-4 left-4">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => handleSelectSubject(subject._id)}
            className="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
          />
        </div>
        
        <div className="flex flex-col">
          <div className="flex items-start justify-between gap-3 mb-4 pl-6">
            <div className="flex items-start gap-3 flex-1">
              <div className="flex-shrink-0">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  contextInfo.isAnnual ? 'bg-gradient-to-br from-green-100 to-green-200' :
                  subject.semesterId ? 'bg-gradient-to-br from-blue-100 to-blue-200' :
                  'bg-gradient-to-br from-purple-100 to-purple-200'
                }`}>
                  <span className={`text-lg ${
                    contextInfo.isAnnual ? 'text-green-600' :
                    subject.semesterId ? 'text-blue-600' :
                    'text-purple-600'
                  }`}>
                    {getSubjectIcon(subject)}
                  </span>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-base font-semibold text-gray-900 truncate">{subject.name}</h3>
                  <code className="font-mono bg-gray-100 px-2 py-0.5 rounded text-xs text-gray-700 border border-gray-200">
                    {subject.subjectCode}
                  </code>
                </div>
                {getSubjectTypeBadge(subject)}
                {contextInfo.badge && (
                  <div className="mt-2">
                    {contextInfo.badge}
                  </div>
                )}
              </div>
            </div>
          </div>


          <div className={`grid gap-3 mb-4 ${subject.isCreditBased ? 'grid-cols-3' : 'grid-cols-2'}`}> 
            {subject.isCreditBased && (
              <div className="text-sm">
                <div className="text-xs text-gray-500 mb-1">Credits</div>
                <div className="font-medium text-gray-900">
                  {subject.credits || 0} Cr
                </div>
              </div>
            )}
            <div className="text-sm">
              <div className="text-xs text-gray-500 mb-1">Attendance</div>
              <div className="font-medium text-gray-900">
                {subject.requiresAttendance ? 'Required' : 'Not Required'}
              </div>
            </div>
            <div className="text-sm">
              <div className="text-xs text-gray-500 mb-1">Assessment Type</div>
              <div className="font-medium text-gray-900">
                {subject.assessmentType === 'internal_only' && 'Internal Only'}
                {subject.assessmentType === 'external_only' && 'External Only'}
                {subject.assessmentType === 'both' && 'Both'}
                {!['internal_only','external_only','both'].includes(subject.assessmentType) && '—'}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end border-t border-gray-100 pt-4">
            <div className="flex items-center gap-1">
              <button
                className="text-gray-600 hover:text-gray-800 hover:bg-gray-100 p-2 rounded-lg transition-colors"
                onClick={() => setViewedSubject(subject)}
                title="View Details"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.27 2.943 9.542 7-1.272 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
              <button
                className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-2 rounded-lg transition-colors"
                onClick={() => handleEdit(subject)}
                title="Edit Subject"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                className="text-green-600 hover:text-green-800 hover:bg-green-50 p-2 rounded-lg transition-colors"
                onClick={() => { 
                  setAssigningSubject(subject); 
                  setShowAssignModal(true); 
                }}
                title="Assign Faculty"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </button>
              <button
                className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 p-2 rounded-lg transition-colors"
                onClick={() => handleViewFaculty(subject)}
                title="View Faculty"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </button>
              <button
                onClick={() => setConfirmDeleteId(subject._id)}
                className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded-lg transition-colors"
                disabled={loading}
                title="Delete Subject"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!department || !department._id) {
    return (
      <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border-2 border-dashed border-gray-300">
        <div className="w-20 h-20 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
          <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No Department Selected</h3>
        <p className="text-gray-600 max-w-md mx-auto">
          Please select a department to manage subjects and curriculum.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <ToastContainer position="top-right" autoClose={5000} />
      
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-6 mb-6 text-white shadow-lg">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold mb-1">Subject Management</h1>
            <p className="text-blue-100 opacity-90">
              Manage curriculum for <strong className="font-semibold">{department.name}</strong>
            </p>
          </div>
          <div className="flex items-center gap-3">
            {academicContext.academicYearId && (
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-xl">
                <div className="text-sm font-medium">{getContextDisplayName()}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Academic Context Filter */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6 shadow-sm">
        <AcademicContextSelector
          tenantId={department.tenantId}
          departmentId={department._id}
          onContextChange={handleAcademicContextChange}
          showLabels={true}
          compact={false}
          token={token}
          initialAcademicYearId={academicContext.academicYearId}
          initialSemesterId={academicContext.semesterId}
          initialTermId={academicContext.termId}
        />
      </div>

      {/* Action Bar with Multi-select Actions */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Subjects</h2>
          <p className="text-sm text-gray-600 mt-1">
            {academicContext.academicYearId ? 
              `${subjects.summary.total} subjects for ${getContextDisplayName()}` : 
              'Select an academic context to begin'
            }
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Multi-select actions */}
          {selectedSubjects.size > 0 && (
            <div className="flex items-center gap-2 mr-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
              <span className="text-sm font-medium text-blue-700">
                {selectedSubjects.size} selected
              </span>
              <button
                onClick={handleSelectAll}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-1 rounded-md hover:bg-blue-100"
              >
                {isSelectAll ? 'Deselect All' : 'Select All'}
              </button>
              <button
                onClick={() => setShowBulkDeleteModal(true)}
                className="text-red-600 hover:text-red-800 text-sm font-medium px-3 py-1 rounded-md hover:bg-red-100"
                disabled={loading}
              >
                Delete Selected
              </button>
            </div>
          )}

          <button
            onClick={() => setShowCopySubjectsModal(true)}
            className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-medium py-2.5 px-4 rounded-xl transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Copy Subjects
          </button>

          <button
            onClick={() => setShowBulkUploadModal(true)}
            className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-medium py-2.5 px-4 rounded-xl transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Bulk Upload
          </button>

          <button
            onClick={handleAddSubjectClick}
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium py-2.5 px-4 rounded-xl transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Subject
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-xl shadow-sm">
          <div className="flex items-center gap-3 text-green-800">
            <div className="flex-shrink-0 w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="font-medium">{success}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl shadow-sm">
          <div className="flex items-center gap-3 text-red-800">
            <div className="flex-shrink-0 w-5 h-5 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="font-medium">{error}</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading subjects...</p>
        </div>
      )}

      {/* Subjects Display */}
      {!academicContext.academicYearId ? (
        <div className="text-center py-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border-2 border-dashed border-gray-300">
          <div className="w-20 h-20 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Select Academic Context</h3>
          <p className="text-gray-600 max-w-md mx-auto mb-8">
            Choose an academic year and semester/term to view and manage subjects for that period.
          </p>
        </div>
      ) : subjects.summary.total === 0 ? (
        <div className="text-center py-16 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border-2 border-dashed border-blue-300">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
            <svg className="w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No subjects for this context</h3>
          <p className="text-gray-600 max-w-md mx-auto mb-8">
            Start by creating the first subject for <strong className="text-blue-700">{getContextDisplayName()}</strong>.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => setShowCopySubjectsModal(true)}
              className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-medium py-2.5 px-6 rounded-xl transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy Subjects
            </button>
            <button
              onClick={handleAddSubjectClick}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium py-2.5 px-6 rounded-xl transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Create First Subject
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Select All Checkbox for Desktop */}
          {subjects.summary.total > 0 && (
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
              <input
                type="checkbox"
                id="selectAllSubjects"
                checked={isSelectAll}
                onChange={handleSelectAll}
                className="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
              />
              <label htmlFor="selectAllSubjects" className="text-sm font-medium text-gray-700 cursor-pointer">
                {isSelectAll ? 'Deselect All Subjects' : 'Select All Subjects'}
                {selectedSubjects.size > 0 && (
                  <span className="ml-2 text-blue-600">
                    ({selectedSubjects.size} selected)
                  </span>
                )}
              </label>
            </div>
          )}

          {/* Show terms if not semester-based and terms exist */}
          {!academicContext.isSemesterBased && academicContext.terms.length > 0 ? (
            renderTermGroups()
          ) : (
            <>
              {renderSemesterGroups()}
              {renderAnnualSubjects()}
            </>
          )}
        </div>
      )}

      {/* Subject Modal */}
      <SubjectModal
        isOpen={showSubjectModal}
        onClose={() => {
          setShowSubjectModal(false);
          setForm(initialForm);
          setEditingId(null);
        }}
        title={editingId ? 'Edit Subject' : 'Create New Subject'}
        subject={editingId ? form : null}
        setSubject={setForm}
        onSubmit={handleSubmit}
        loading={loading}
        actionText={editingId ? 'Update Subject' : 'Create Subject'}
        editMode={!!editingId}
        tenantId={department.tenantId}
        departmentId={department._id}
        token={token}
        initialAcademicYearId={editingId ? (form.academicYearId || academicContext.academicYearId) : academicContext.academicYearId}
        initialSemesterId={editingId ? (form.semesterId || academicContext.semesterId) : academicContext.semesterId}
        initialTermId={editingId ? (form.termId || academicContext.termId) : academicContext.termId}
        key={`subject-modal-${editingId || 'create'}`}
      />

      {/* Delete Confirmation Modal */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Subject</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete this subject? All associated faculty assignments and student enrollments will be removed.
            </p>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                onClick={() => setConfirmDeleteId(null)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2.5 text-sm font-medium text-white bg-red-600 border border-transparent rounded-xl hover:bg-red-700 transition-colors disabled:opacity-60"
                onClick={() => handleDelete(confirmDeleteId)}
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Delete Subject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Selected Subjects</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete {selectedSubjects.size} selected subject{selectedSubjects.size !== 1 ? 's' : ''}? 
              All associated faculty assignments and student enrollments will be removed.
            </p>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2.5 text-sm font-medium text-white bg-red-600 border border-transparent rounded-xl hover:bg-red-700 transition-colors disabled:opacity-60 flex items-center gap-2"
                onClick={handleBulkDelete}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  `Delete ${selectedSubjects.size} Subject${selectedSubjects.size !== 1 ? 's' : ''}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Subject Modal */}
      {viewedSubject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                <span className="text-blue-600 text-xl">
                  {getSubjectIcon(viewedSubject)}
                </span>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{viewedSubject.name || '—'}</h3>
                    <p className="text-sm text-gray-500 mt-1">{viewedSubject.subjectCode || '—'}</p>
                  </div>
                  <button
                    onClick={() => setViewedSubject(null)}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="mt-4 space-y-3 text-sm text-gray-700">
                  <div>
                    <div className="text-xs text-gray-500">Type</div>
                    <div className="font-medium">{viewedSubject.subjectType || 'Theory'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Category</div>
                    <div className="font-medium">{viewedSubject.subjectCategory || 'Academic'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Management Type</div>
                    <div className="font-medium">
                      {(viewedSubject.subjectType === 'elective' || viewedSubject.subjectType === 'language')
                        ? (viewedSubject.requiresGroupManagement ? 'Choice-Based' : 'Common')
                        : 'N/A'}
                      {viewedSubject.electiveType && ` (${viewedSubject.electiveType})`}
                      {viewedSubject.languageType && ` (${viewedSubject.languageType})`}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Credits</div>
                    <div className="font-medium">{viewedSubject.isCreditBased ? `${viewedSubject.credits || 0} Cr` : 'Non-Credit'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Academic Context</div>
                    <div className="font-medium">
                      {viewedSubject.semesterId ? 'Semester Based' : 
                       viewedSubject.termId ? 'Term Based' : 'Annual'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Created</div>
                    <div className="font-medium">{viewedSubject.createdAt ? new Date(viewedSubject.createdAt).toLocaleDateString() : '—'}</div>
                  </div>
                </div>
                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setViewedSubject(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Faculty Assignment Modal */}
      <SubjectFacultyAssignment
        isOpen={showAssignModal}
        subject={assigningSubject}
        department={department}
        onClose={() => { 
          setShowAssignModal(false); 
          setAssigningSubject(null); 
        }}
        onAssign={async (subject, facultyIds) => {
          if (onAssignFacultyToSubject) {
            await onAssignFacultyToSubject(subject, facultyIds);
          } else {
            await api.put(`/api/subjects/assign-faculty/${subject._id}`, {
              facultyIds,
              tenantId: department.tenantId
            });
          }
          await fetchSubjects(true);
          toast.success('Faculty assigned successfully');
        }}
      />

      {/* View Faculty Modal */}
      {showFacultyModal && viewFacultySubject && (
        <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center pt-20 md:pt-0 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-2xl w-full mx-4">
            <div className="flex items-start md:items-center justify-between gap-4 mb-4">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 truncate">Assigned Faculty — {viewFacultySubject.name}</h3>
                <p className="text-sm text-gray-500 mt-1">Faculty currently assigned to this subject ({assignedFacultyList.length})</p>
              </div>
              <button
                onClick={() => { 
                  setShowFacultyModal(false); 
                  setViewFacultySubject(null); 
                  setFacultySearch(''); 
                }}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-md hover:bg-gray-100"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <input
                value={facultySearch}
                onChange={(e) => setFacultySearch(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full border border-gray-300 rounded-lg py-2.5 px-4 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-colors"
              />
            </div>

            <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
              {viewLoading ? (
                <div className="py-8 text-center text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  Loading assigned faculty…
                </div>
              ) : (
                assignedFacultyList
                  .filter(f => {
                    const q = facultySearch.trim().toLowerCase();
                    if (!q) return true;
                    return (f.name || '').toLowerCase().includes(q) || (f.email || '').toLowerCase().includes(q);
                  })
                  .map(f => (
                    <div key={f._id} className="flex items-center justify-between gap-4 p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center text-blue-700 font-semibold">
                          {(f.name || '').split(' ').map(n => n[0]).slice(0,2).join('').toUpperCase() || 'U'}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{f.name || 'Unknown'}</div>
                          <div className="text-xs text-gray-500 truncate">{f.email || '—'}</div>
                        </div>
                      </div>
                      <button
                        onClick={() => setConfirmRemoveId(f._id)}
                        className="text-red-600 hover:text-red-800 text-sm px-3 py-1.5 border border-red-200 rounded-lg bg-red-50 hover:bg-red-100 transition-colors"
                        disabled={viewLoading}
                      >
                        {viewLoading ? 'Removing…' : 'Remove'}
                      </button>
                    </div>
                  ))
              )}
              {!viewLoading && assignedFacultyList.length === 0 && (
                <div className="py-8 text-center text-sm text-gray-500">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                  </div>
                  No faculty assigned to this subject.
                </div>
              )}
            </div>

            {viewError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2 text-red-700 text-sm">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span>{viewError}</span>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => { 
                  setShowFacultyModal(false); 
                  setViewFacultySubject(null); 
                  setFacultySearch(''); 
                }}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50"
              >
                Close
              </button>
            </div>

            {/* Confirm remove dialog */}
            {confirmRemoveId && (
              <div className="mt-4 p-4 border-t border-gray-100">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">Remove faculty from subject?</div>
                    <div className="text-xs text-gray-500 mt-1">This will unassign the faculty from <strong>{viewFacultySubject.name}</strong>.</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setConfirmRemoveId(null)}
                      className="px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
                      disabled={viewLoading}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        await removeAssignedFaculty(confirmRemoveId);
                      }}
                      className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                      disabled={viewLoading}
                    >
                      {viewLoading ? 'Removing…' : 'Confirm Remove'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        tenantId={department.tenantId}
        department={department}
        createdBy={getUserIdFromStorage()}
        refreshData={() => fetchSubjects(true)}
        currentAcademicContext={academicContext}
      />

      {/* Copy Subjects Modal */}
      <CopySubjectsModal
        isOpen={showCopySubjectsModal}
        onClose={() => setShowCopySubjectsModal(false)}
        tenantId={department.tenantId}
        department={department}
        currentAcademicContext={academicContext}
        refreshData={() => fetchSubjects(true)}
      />
    </div>
  );
};

export default SubjectManager;